import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import type { TFunction } from '@mukil/i18n';
import {
  AdaptiveDialog,
  Alert,
  Button,
  DatePicker,
  FormField,
  Input,
  MoneyInput,
  StyledSelect,
} from '@jumpstone/ui-react';
import type { SelfLinkResult } from '../../data/use_self_link';
import { useSettings } from '../../data/use_settings';
import { useDatePickerLabels } from '../../lib/use_date_picker_labels';
import { SELECT_TRIGGER_CLASSES } from '../employees/employee_form_fields';
import {
  buildSelfLinkProfileInput,
  emptySelfLinkProfileDraft,
  validateSelfLinkProfile,
  type SelfLinkProfileDraft,
  type SelfLinkProfileErrors,
  type SelfLinkProfileFieldError,
  type SelfLinkProfileInput,
} from './my_pay_self_link_form';

/** Stable no-op: blocks dismissal while a submit is in flight. */
function noop(): void {
  // intentionally empty
}

type SelfLinkFailureResult = Extract<SelfLinkResult, { outcome: 'failure' }>;

export interface MyPaySelfLinkDialogProps {
  open: boolean;
  onClose: () => void;
  /** Retries `payroll.linkSelfAsEmployee` with the collected profile. */
  submit: (profile: SelfLinkProfileInput) => Promise<SelfLinkResult>;
  /** Called after the server created the record and linked it. */
  onLinked: () => void;
}

/** Literal-per-branch so the static i18n extractor sees every key. */
function fieldErrorMessage(
  t: TFunction,
  error: SelfLinkProfileFieldError | undefined,
): string | undefined {
  switch (error) {
    case undefined:
      return undefined;
    case 'required':
      return t('employees.form_error_required');
    case 'negative_amount':
      return t('employees.form_error_negative_amount');
    case 'salary_required':
      return t('employees.form_error_salary_required');
    case 'rate_required':
      return t('my_pay.link_self_error_rate_required');
  }
}

/**
 * Collects the pay details `payroll.linkSelfAsEmployee` needs to create an
 * employer's own employee record (pay basis, rate or salary, start date) when
 * none exists yet. Draft + validation live in `my_pay_self_link_form.ts`.
 */
export function MyPaySelfLinkDialog({
  open,
  onClose,
  submit,
  onLinked,
}: MyPaySelfLinkDialogProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { organisation } = useSettings();
  const datePickerLabels = useDatePickerLabels();
  const [draft, setDraft] = React.useState<SelfLinkProfileDraft>(
    emptySelfLinkProfileDraft,
  );
  const [errors, setErrors] = React.useState<SelfLinkProfileErrors>({});
  const [failed, setFailed] = React.useState<SelfLinkFailureResult | null>(
    null,
  );
  const [submitting, setSubmitting] = React.useState(false);

  function patch(next: Partial<SelfLinkProfileDraft>): void {
    setDraft((d) => ({ ...d, ...next }));
  }

  async function handleSubmit(): Promise<void> {
    const found = validateSelfLinkProfile(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSubmitting(true);
    setFailed(null);
    const result = await submit(buildSelfLinkProfileInput(draft));
    setSubmitting(false);
    if (result.outcome === 'success') {
      onLinked();
      return;
    }
    setFailed(result);
  }

  const currency = organisation.base_currency;
  return (
    <AdaptiveDialog
      open={open}
      onClose={submitting ? noop : onClose}
      maxWidth="lg"
      title={t('my_pay.link_self_profile_title')}
      description={t('my_pay.link_self_profile_description')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            onClick={() => void handleSubmit()}
            data-testid="my-pay-self-link-submit"
          >
            {t('my_pay.link_self_profile_submit')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <FormField label={t('employees.form_pay_basis')}>
          <StyledSelect
            value={draft.pay_basis}
            onChange={(pay_basis) => {
              patch({ pay_basis });
              setErrors((e) => ({ ...e, amount: undefined }));
            }}
            options={[
              {
                value: 'hourly',
                label: t('employees.form_pay_basis_hourly'),
              },
              {
                value: 'salaried',
                label: t('employees.form_pay_basis_salaried'),
              },
            ]}
            ariaLabel={t('employees.form_pay_basis')}
            triggerClassName={SELECT_TRIGGER_CLASSES}
          />
        </FormField>
        <FormField
          label={
            draft.pay_basis === 'salaried'
              ? t('employees.form_monthly_salary', { currency })
              : t('employees.form_hourly_rate_currency', { currency })
          }
          htmlFor="my-pay-self-link-amount"
          error={fieldErrorMessage(t, errors.amount)}
        >
          <MoneyInput
            id="my-pay-self-link-amount"
            value={draft.amount_cents}
            onChange={(amount_cents) => {
              patch({ amount_cents });
            }}
            data-testid="my-pay-self-link-amount"
          />
        </FormField>
        <FormField
          label={t('employees.form_start_date')}
          htmlFor="my-pay-self-link-start-date"
          error={fieldErrorMessage(t, errors.employment_start_date)}
        >
          <DatePicker
            id="my-pay-self-link-start-date"
            data-testid="my-pay-self-link-start-date"
            value={draft.employment_start_date || null}
            locale={locale}
            labels={datePickerLabels}
            aria-label={t('employees.form_start_date')}
            error={Boolean(errors.employment_start_date)}
            onChange={(employment_start_date) => {
              patch({ employment_start_date });
            }}
          />
        </FormField>
        <FormField
          label={t('employees.form_job_title')}
          htmlFor="my-pay-self-link-job-title"
        >
          <Input
            id="my-pay-self-link-job-title"
            data-testid="my-pay-self-link-job-title"
            value={draft.job_title}
            onChange={(e) => {
              patch({ job_title: e.target.value });
            }}
          />
        </FormField>
      </div>

      {failed?.failure === 'plan_cap' && (
        <Alert
          variant="warning"
          title={t('employees.form_plan_cap_title')}
          className="mt-4"
        >
          {failed.message || t('employees.form_plan_cap_fallback')}
        </Alert>
      )}
      {failed?.failure === 'validation' && (
        <Alert
          variant="danger"
          title={t('employees.form_validation_error_title')}
          className="mt-4"
        >
          {failed.message}
        </Alert>
      )}
      {failed !== null &&
        failed.failure !== 'plan_cap' &&
        failed.failure !== 'validation' && (
          <Alert variant="danger" className="mt-4">
            {t('my_pay.link_self_error_generic')}
          </Alert>
        )}
    </AdaptiveDialog>
  );
}
